
import { BillingType, UserType, Plan } from '../types/plan';
import { collection, getDocs, query } from 'firebase/firestore';


/**
 * Classe utilitária para lógica de planos (Single Responsibility)
 */
export class PlanService {
  // Formatar período do plano
  static formatPeriod(quantity: number, billingType: BillingType, isRecurring: boolean): string {
    const sessionLabel = quantity === 1 ? 'sessão' : 'sessões';
    const hourLabel = quantity === 1 ? 'hora' : 'horas';
    const label = billingType === BillingType.SESSIONS ? sessionLabel : hourLabel;
    
    if (isRecurring) {
      return `${quantity} ${label} por mês`;
    }
    return `${quantity} ${label}`;
  }

  // Obter texto do botão baseado no tipo de plano
  static getButtonText(isRecurring: boolean): string {
    return isRecurring ? 'Assinar Plano' : 'Comprar Agora';
  }

  // Formatar valor como moeda brasileira
  static formatCurrency(value: number): string {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // Obter label do tipo de cobrança
  static getBillingTypeLabel(billingType: BillingType, plural: boolean = true): string {
    if (billingType === BillingType.SESSIONS) {
      return plural ? 'Sessões' : 'Sessão';
    }
    return plural ? 'Horas' : 'Hora';
  }

  // Obter label do tipo de usuário
  static getUserTypeLabel(userType: UserType): string {
    if (userType === UserType.PATIENT) return 'Paciente';
    if (userType === UserType.COURSE) return 'Cursos, mentorias e vivências';
    return 'Terapeuta';
  }

  /** Normaliza billingType vindo do Firestore (hours/HOURS → hours). */
  static normalizeBillingType(value: unknown): BillingType | null {
    if (typeof value !== 'string') return null;
    const normalized = value.trim().toLowerCase();
    if (normalized === BillingType.HOURS) return BillingType.HOURS;
    if (normalized === BillingType.SESSIONS) return BillingType.SESSIONS;
    return null;
  }

  /** Plano permite escolher quantidade de horas antes do checkout. */
  static supportsCustomQuantity(plan: Pick<Plan, 'allowCustomQuantity' | 'billingType' | 'isRecurring' | 'quantity' | 'value'>): boolean {
    const billingType = PlanService.normalizeBillingType(plan.billingType) ?? plan.billingType;
    return (
      !!plan.allowCustomQuantity &&
      billingType === BillingType.HOURS &&
      !plan.isRecurring &&
      (plan.quantity || 0) > 0 &&
      (plan.value || 0) > 0
    );
  }

  /** Ex.: "4 horas · R$ 17,47/hora" */
  static formatHourRateLine(quantity: number, value: number): string {
    const hoursLabel = quantity === 1 ? '1 hora' : `${quantity} horas`;
    const perHour = value / quantity;
    return `${hoursLabel} · ${PlanService.formatCurrency(perHour)}/hora`;
  }

  /** Remove linha de preço/hora duplicada no início da descrição do plano. */
  static stripHoursPricingFromDescription(description: string): string {
    return description
      .replace(/^\d+\s*horas?,?\s*R\$\s*[\d.,]+\s*a\s*hora\.?\s*\n*/i, '')
      .trimStart();
  }

  // Mapear dados do Firestore para Plan
  static fromFirestore(id: string, data: any): Plan {
    const billingType = PlanService.normalizeBillingType(data.billingType) ?? (data.billingType as BillingType);
    return {
      id,
      name: data.name || '',
      value: data.value || 0,
      billingType,
      quantity: data.quantity || 0,
      userType: data.userType as UserType,
      isRecurring: data.isRecurring || false,
      description: data.description || '',
      isFeatured: data.isFeatured || false,
      sessionLabel: data.sessionLabel || '',
      features: data.features || [],
      deleted: data.deleted || false,
      therapistCommission: data.therapistCommission ?? 0,
      platformFee: data.platformFee ?? 100,
      allowCustomQuantity: !!data.allowCustomQuantity,
      minQuantity: Number.isInteger(data.minQuantity) ? data.minQuantity : undefined,
      maxQuantity: Number.isInteger(data.maxQuantity) ? data.maxQuantity : undefined,
      cycle: data.cycle === "YEARLY" ? "YEARLY" : "MONTHLY",
      allowPixInstallments: !!data.allowPixInstallments,
      maxPixInstallments: Number.isInteger(data.maxPixInstallments) ? data.maxPixInstallments : 12,
      cancellable: data.cancellable !== false,
      homologationMonths: Number(data.homologationMonths) > 0 ? Number(data.homologationMonths) : 0,
      homologationOnly: !!data.homologationOnly,
    };
  }

  // Preparar dados para salvar no Firestore
  static toFirestore(plan: Omit<Plan, 'id'>): any {
    return {
      name: plan.name,
      value: plan.value,
      billingType: plan.billingType,
      quantity: plan.quantity,
      userType: plan.userType,
      isRecurring: plan.isRecurring,
      description: plan.description,
      isFeatured: plan.isFeatured || false,
      sessionLabel: plan.sessionLabel || '',
      features: plan.features || [],
      deleted: plan.deleted || false,
      therapistCommission: plan.therapistCommission ?? 0,
      platformFee: plan.platformFee ?? 100,
      allowCustomQuantity: !!plan.allowCustomQuantity,
      minQuantity: Number.isInteger(plan.minQuantity) ? plan.minQuantity : null,
      maxQuantity: Number.isInteger(plan.maxQuantity) ? plan.maxQuantity : null,
      cycle: plan.cycle === "YEARLY" ? "YEARLY" : "MONTHLY",
      allowPixInstallments: !!plan.allowPixInstallments,
      maxPixInstallments: Number.isInteger(plan.maxPixInstallments) ? plan.maxPixInstallments : 12,
      cancellable: plan.cancellable !== false,
      homologationMonths: Number(plan.homologationMonths) > 0 ? Number(plan.homologationMonths) : 0,
      homologationOnly: !!plan.homologationOnly,
    };
  }

  // Buscar planos não deletados do Firestore
  static async fetchActivePlans(db: any): Promise<Plan[]> {
    // Importações estáticas para garantir compatibilidade
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    // @ts-ignore
    // eslint-disable-next-line import/no-extraneous-dependencies
    // Importar do firebase/firestore
    // (já está importado no topo do projeto, mas garantir aqui)
    // import { collection, getDocs, query } from 'firebase/firestore';
    const plansQuery = query(collection(db, 'plans'));
    const snapshot = await getDocs(plansQuery);
    let plansList: Plan[] = snapshot.docs.map((doc: any) => PlanService.fromFirestore(doc.id, doc.data()));
    plansList = plansList.filter((plan: Plan) => !plan.deleted);
    plansList.sort((a: Plan, b: Plan) => a.name.localeCompare(b.name));
    return plansList;
  }
}
